/**
 * The server's clock, as seen from the browser.
 *
 * WHY THIS EXISTS – the device clock is user-controlled and can be wrong by an
 * hour or more (seen 2026-10-11: Android phone set to Europe/Warsaw with the
 * time pushed forward by hand to show Kyiv time, i.e. +59.5 min). Every
 * deadline this app handles – access-token expiry, test windows, event start –
 * is issued by a server in server time. Comparing it with `Date.now()` on such
 * a device gives the wrong answer: the browser's auth-js saw every fresh token
 * as expiring, refreshed ~150 ms apart until GoTrue answered 429, and signed
 * the user out 5–10 s after each login. (The browser no longer runs auth-js;
 * the access-token manager in src/lib/auth/accessToken.ts uses this clock.)
 *
 * We measure `offset = serverTime − deviceTime` and expose `serverNow()`. The
 * offset lives on `globalThis.__CONFY_CLOCK_OFFSET_MS__`.
 *
 * Offset sources, newest wins:
 *  1. Boot – an inline script in the root layout runs before any app code and
 *     records the server render time (serverClockBootScript), so the first
 *     token-expiry and deadline checks on page load already use server time.
 *  2. Every auth token response – GoTrue returns `expires_at` (server epoch) and
 *     `expires_in`, so server-now ≈ expires_at − expires_in, accurate to ~1 s
 *     plus one round trip (recordServerTimeFromSession, via the token route in
 *     src/lib/auth/accessToken.ts).
 *
 * Offsets under NOISE_THRESHOLD_MS are treated as zero: the boot sample includes
 * HTML transit time, and a few seconds is irrelevant next to the token
 * manager's 60 s refresh margin. On the server the global is never set, so serverNow() is
 * exactly Date.now().
 *
 * Known limit: if the user corrects the device clock while the page is open,
 * the offset is stale until the next token response (≤ 1 h). The skew notice
 * asks the user to reload after fixing the clock.
 */

declare global {
  // eslint-disable-next-line no-var
  var __CONFY_CLOCK_OFFSET_MS__: number | undefined
}

const NOISE_THRESHOLD_MS = 10_000

/** Skew at which the user is told their device clock is wrong. */
export const CLOCK_SKEW_NOTICE_THRESHOLD_MS = 5 * 60_000

type Listener = (offsetMs: number) => void
const listeners = new Set<Listener>()

function normalise(rawOffsetMs: number): number {
  return Math.abs(rawOffsetMs) < NOISE_THRESHOLD_MS ? 0 : Math.round(rawOffsetMs)
}

/** server time − device time, in ms. 0 when unknown or within noise. */
export function getClockOffsetMs(): number {
  const offset = globalThis.__CONFY_CLOCK_OFFSET_MS__
  return typeof offset === 'number' && Number.isFinite(offset) ? offset : 0
}

/** Current epoch ms on the server's clock. Use instead of Date.now() whenever
 * the result is compared with a server-issued timestamp. */
export function serverNow(): number {
  return Date.now() + getClockOffsetMs()
}

/** `new Date()` on the server's clock. */
export function serverDate(): Date {
  return new Date(serverNow())
}

/** Record a sample of the server's current time (epoch ms). */
export function recordServerTime(serverEpochMs: number, deviceEpochMs: number = Date.now()): void {
  if (!Number.isFinite(serverEpochMs) || serverEpochMs <= 0) return
  const next = normalise(serverEpochMs - deviceEpochMs)
  const previous = getClockOffsetMs()
  globalThis.__CONFY_CLOCK_OFFSET_MS__ = next
  if (next !== previous) {
    listeners.forEach((listener) => listener(next))
  }
}

/**
 * Record server time from a GoTrue session/token response body. Returns false
 * when the body does not carry both `expires_at` and `expires_in`.
 */
export function recordServerTimeFromSession(
  body: unknown,
  deviceEpochMs: number = Date.now()
): boolean {
  if (!body || typeof body !== 'object') return false
  const { expires_at, expires_in } = body as { expires_at?: unknown; expires_in?: unknown }
  if (typeof expires_at !== 'number' || typeof expires_in !== 'number') return false
  recordServerTime((expires_at - expires_in) * 1000, deviceEpochMs)
  return true
}

export function subscribeClockOffset(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Inline script for the root layout <head>. Runs during HTML parse, before any
 * Next.js chunk, so the first token and deadline checks already use server time.
 * Must stay self-contained (no imports) – it is serialised into the HTML.
 */
export function serverClockBootScript(serverEpochMs: number): string {
  const server = Math.round(serverEpochMs)
  return `(function(){var o=${server}-Date.now();globalThis.__CONFY_CLOCK_OFFSET_MS__=Math.abs(o)<${NOISE_THRESHOLD_MS}?0:o})();`
}
